import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CarFront, 
  ClipboardCheck, 
  BadgeCheck, 
  TrendingDown, 
  CalendarX, 
  Camera,
  ShieldCheck,
  FileCheck,
  Zap,
  ArrowRight,
  Phone,
  CheckCircle,
  MessageCircle
} from 'lucide-react';
import StatusBar from '../components/StatusBar';
import { services, trustPoints, processSteps, contactData } from '../data/mock';

const HomePage = () => {
  const getServiceIcon = (iconName) => {
    const icons = {
      'car-crash': CarFront,
      'clipboard-check': ClipboardCheck,
      'badge-check': BadgeCheck,
      'trending-down': TrendingDown,
      'calendar-x': CalendarX,
      'camera': Camera
    };
    return icons[iconName] || CarFront;
  };

  const getTrustIcon = (iconName) => {
    const icons = {
      'shield-check': ShieldCheck,
      'file-check': FileCheck,
      'zap': Zap
    };
    return icons[iconName] || ShieldCheck;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}`, '_blank');
  };

  return (
    <div style={{ background: '#FFFFFF', position: 'relative' }}>
      <div className="pattern-overlay" />
      
      {/* Premium Hero Section - Full Screen */}
      <section style={{ 
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden'
      }}>
        {/* Hero Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0
        }}>
          <img 
            src="https://images.pexels.com/photos/3764984/pexels-photo-3764984.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Hochwertige Fahrzeugbegutachtung im Rhein-Main-Gebiet – Kfz Gutachter Crash Check"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }}
          />
          {/* Light Friendly Overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(236, 254, 255, 0.9) 100%)'
          }} />
        </div>

        <div className="container mx-auto px-4" style={{ maxWidth: '1400px', position: 'relative', zIndex: 2, paddingTop: '100px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              {/* Premium Badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(8, 145, 178, 0.1)',
                border: '1px solid rgba(8, 145, 178, 0.3)',
                padding: '10px 20px',
                borderRadius: '50px',
                marginBottom: '32px',
                animation: 'fade-in-up 0.8s ease-out'
              }}>
                <CheckCircle size={18} style={{ color: '#0891B2' }} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#0891B2',
                  letterSpacing: '0.5px'
                }}>
                  UNABHÄNGIGE GUTACHTER
                </span>
              </div>

              <h1 style={{ 
                fontSize: 'clamp(42px, 7vw, 72px)',
                fontWeight: '800',
                lineHeight: '1.1',
                letterSpacing: '-0.02em',
                color: '#1a202c',
                marginBottom: '28px',
                animation: 'fade-in-up 0.8s ease-out 0.1s both'
              }}>
                Unfallgutachten.<br/>
                <span style={{ 
                  background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Digital. Schnell. Präzise.
                </span>
              </h1>
              
              <p style={{ 
                fontSize: '20px',
                fontWeight: '400',
                color: '#4a5568',
                lineHeight: '1.7',
                maxWidth: '560px',
                marginBottom: '48px',
                animation: 'fade-in-up 0.8s ease-out 0.2s both'
              }}>
                Schadensdokumentation vor Ort. Rechtssichere Gutachten innerhalb von 24 Stunden. Für Versicherung und Anwalt.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4" style={{ 
                marginBottom: '60px',
                animation: 'fade-in-up 0.8s ease-out 0.3s both'
              }}>
                <Link to="/kontakt" className="btn-primary" style={{ minHeight: '64px', fontSize: '18px', padding: '0 40px' }}>
                  Jetzt Gutachten anfragen
                  <ArrowRight size={22} />
                </Link>
                <button onClick={handleWhatsApp} className="btn-secondary" style={{ minHeight: '64px', fontSize: '18px', padding: '0 40px' }}>
                  <MessageCircle size={20} />
                  WhatsApp Soforthilfe
                </button>
              </div>

              {/* Trust Indicators */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '32px',
                animation: 'fade-in-up 0.8s ease-out 0.4s both'
              }}>
                {[
                  { icon: ShieldCheck, text: '24/7 Erreichbar' },
                  { icon: Zap, text: 'Vor-Ort Service' },
                  { icon: FileCheck, text: 'Rechtssicher' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: 'rgba(8, 145, 178, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon size={20} style={{ color: '#0891B2' }} />
                      </div>
                      <span style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1a202c'
                      }}>
                        {item.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side - Stats Cards */}
            <div className="hidden lg:grid" style={{
              gridTemplateColumns: '1fr 1fr',
              gap: '24px',
              animation: 'fade-in-up 0.8s ease-out 0.5s both'
            }}>
              {[
                { number: '1000+', label: 'Gutachten erstellt' },
                { number: '24h', label: 'Durchschnittliche Bearbeitungszeit' },
                { number: '100%', label: 'Rechtssicher' },
                { number: '5★', label: 'Kundenbewertung' }
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="glass-card"
                  style={{
                    padding: '32px',
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div style={{
                    fontSize: '36px',
                    fontWeight: '800',
                    background: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: '8px'
                  }}>
                    {stat.number}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#718096',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          animation: 'fade-in-up 1s ease-out 0.6s both'
        }}>
          <div style={{
            width: '30px',
            height: '50px',
            border: '2px solid rgba(0, 168, 204, 0.5)',
            borderRadius: '20px',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '8px'
          }}>
            <div style={{
              width: '4px',
              height: '10px',
              background: '#00A8CC',
              borderRadius: '2px',
              animation: 'scroll-bounce 2s infinite'
            }} />
          </div>
        </div>
      </section>

      {/* Status Bar */}
      <div style={{ 
        marginTop: '48px', 
        marginBottom: '48px',
        position: 'relative', 
        zIndex: 10 
      }}>
        <StatusBar />
      </div>

      {/* Services Section - Premium Grid */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1400px' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px', maxWidth: '700px', margin: '0 auto 80px' }}>
            <h2 style={{
              fontSize: 'clamp(32px, 5vw, 52px)',
              fontWeight: '700',
              lineHeight: '1.2',
              color: '#1a202c',
              marginBottom: '20px'
            }}>
              Unsere Leistungen
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#4a5568',
              lineHeight: '1.7'
            }}>
              Gutachten für jeden Bedarf – schnell, präzise und rechtssicher
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = getServiceIcon(service.icon);
              return (
                <Link
                  key={service.id}
                  to="/dienstleistungen"
                  className="glass-card"
                  style={{
                    padding: '40px',
                    textDecoration: 'none',
                    display: 'block',
                    animation: `fade-in-up 0.6s ease-out ${index * 0.1}s both`,
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px'
                  }}>
                    <Icon size={32} style={{ color: '#0891B2' }} />
                  </div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1a202c',
                    marginBottom: '12px'
                  }}>
                    {service.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: '#4a5568',
                    lineHeight: '1.7',
                    marginBottom: '20px'
                  }}>
                    {service.description}
                  </p>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#0891B2',
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'gap 0.3s ease'
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.gap = '12px'}
                    onMouseLeave={(e) => e.currentTarget.style.gap = '8px'}
                  >
                    Mehr erfahren
                    <ArrowRight size={18} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Section - Modern Car */}
      <section style={{ 
        position: 'relative',
        height: '500px',
        overflow: 'hidden',
        margin: '100px 0'
      }}>
        <img 
          src="https://images.pexels.com/photos/35612817/pexels-photo-35612817.jpeg?auto=compress&cs=tinysrgb&w=1920&h=500&fit=crop"
          alt="Moderne Fahrzeuginspektion und Schadensdokumentation"
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
          }}
        />
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(236, 254, 255, 0.9) 100%)',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div className="container mx-auto px-4" style={{ maxWidth: '1400px' }}>
            <div style={{ maxWidth: '600px' }}>
              <h2 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                color: '#1a202c',
                marginBottom: '20px'
              }}>
                Modernste Technologie trifft auf jahrelange Erfahrung
              </h2>
              <p style={{
                fontSize: '18px',
                color: '#4a5568',
                lineHeight: '1.7',
                marginBottom: '32px'
              }}>
                Digitale Schadensdokumentation mit höchster Präzision. Jedes Detail wird erfasst.
              </p>
              <Link to="/uber-uns" className="btn-primary">
                Mehr über uns
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontSize: 'clamp(32px, 5vw, 52px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '20px'
            }}>
              So einfach geht's
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#4a5568'
            }}>
              Ihr Weg zum fertigen Gutachten in 4 Schritten
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={step.id}
                style={{
                  position: 'relative',
                  textAlign: 'center',
                  animation: `fade-in-up 0.6s ease-out ${index * 0.15}s both`
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)',
                  border: '2px solid rgba(8, 145, 178, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  fontWeight: '800',
                  color: '#0891B2',
                  margin: '0 auto 24px',
                  position: 'relative'
                }}>
                  {step.id}
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block" style={{
                      position: 'absolute',
                      left: '100%',
                      top: '50%',
                      width: 'calc(100% + 32px)',
                      height: '2px',
                      background: 'linear-gradient(90deg, rgba(8, 145, 178, 0.3), rgba(8, 145, 178, 0.1))',
                      transform: 'translateY(-50%)'
                    }} />
                  )}
                </div>
                
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1a202c',
                  marginBottom: '12px'
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: '#4a5568',
                  lineHeight: '1.6'
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1000px' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 168, 204, 0.1) 0%, rgba(0, 255, 209, 0.05) 100%)',
            border: '1px solid rgba(0, 168, 204, 0.3)',
            borderRadius: '24px',
            padding: '80px 60px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(0, 168, 204, 0.15) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(80px)'
            }} />
            
            <h2 style={{
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: '700',
              color: '#1a202c',
              marginBottom: '24px',
              position: 'relative',
              zIndex: 2
            }}>
              Unfall gehabt?<br/>
              Wir übernehmen ab hier.
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#4a5568',
              lineHeight: '1.7',
              marginBottom: '48px',
              maxWidth: '700px',
              margin: '0 auto 48px',
              position: 'relative',
              zIndex: 2
            }}>
              Kontaktieren Sie uns jetzt und erhalten Sie Ihr Gutachten innerhalb von 24 Stunden.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" style={{ position: 'relative', zIndex: 2 }}>
              <Link to="/kontakt" className="btn-primary" style={{ minHeight: '64px', fontSize: '18px', padding: '0 40px' }}>
                Jetzt Gutachten anfragen
                <ArrowRight size={22} />
              </Link>
              <button onClick={handleWhatsApp} className="btn-secondary" style={{ minHeight: '64px', fontSize: '18px', padding: '0 40px' }}>
                WhatsApp Soforthilfe
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes scroll-bounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(10px); opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
